import { Scene, SceneItem, AspectRatio, SceneItemType } from '@monorepo/types';
import React, { FunctionComponent, useState, useEffect } from 'react';
import {
  Button,
  Typography,
  Tooltip,
  Slider,
  Card,
  Row,
  Col,
  Popover,
} from 'antd';
import { LayoutOutlined } from '@ant-design/icons';
import { useVideoContext } from '../../../../hooks/use-video';
import { useTranslation } from 'react-i18next';

import './scene-item-position.scss';
import { aspectRatioDimensions } from '@monorepo/remotion-ninja';

export type SceneItemPositionValues = {
  left: number;
  top: number;
  width: number;
  height: number;
};

interface Props {
  scene: Scene;
  sceneItem: SceneItem;
}

const SceneItemPosition: FunctionComponent<Props> = ({ scene, sceneItem }) => {
  const { t } = useTranslation();
  const { updateSceneItem, video } = useVideoContext();

  const ratios =
    aspectRatioDimensions[video?.aspectRatio || AspectRatio.NineBySixteen];

  const parseInitialValue = (
    value: string | number | undefined,
    defaultValue: number
  ): number => {
    if (value === undefined) return defaultValue;
    if (typeof value === 'number') return value;
    // Try to extract numeric part from strings like "50px" or "50%"
    const numericValue = parseFloat(value.toString());
    return isNaN(numericValue) ? defaultValue : numericValue;
  };

  const [values, setValues] = useState<SceneItemPositionValues>({
    left: parseInitialValue(sceneItem.left, 0),
    top: parseInitialValue(sceneItem.top, 0),
    height: parseInitialValue(sceneItem.height, ratios.compositionHeight),
    width: parseInitialValue(sceneItem.width, ratios.compositionWidth),
  });

  useEffect(() => {
    setValues({
      left: parseInitialValue(sceneItem.left, 0),
      top: parseInitialValue(sceneItem.top, 0),
      height: parseInitialValue(sceneItem.height, ratios.compositionHeight),
      width: parseInitialValue(sceneItem.width, ratios.compositionWidth),
    });
  }, [sceneItem, ratios]);

  const handleSliderChange = (
    field: keyof SceneItemPositionValues,
    value: number
  ) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSliderComplete = async (
    field: keyof SceneItemPositionValues,
    value: number
  ) => {
    await updateSceneItem({
      sceneId: scene._id,
      videoId: scene.videoId,
      itemIndex: sceneItem.index,
      updateDto: {
        [field]: value,
      },
    });
  };

  const handleQuickChange = (
    field: keyof SceneItemPositionValues,
    percentage: number
  ) => {
    let baseValue: number;

    if (field === 'width' || field === 'left') {
      baseValue = ratios.compositionWidth;
    } else {
      baseValue = ratios.compositionHeight;
    }

    const newValue = Math.round(baseValue * (percentage / 100));

    handleSliderChange(field, newValue);
    handleSliderComplete(field, newValue);
  };

  const positionDropdownContent = (
    <Card className="scene-item-position-card">
      <Typography.Title style={{ padding: 0, margin: 0 }} level={5}>
        {t('videoEditor.sceneItem.positionSize')}
      </Typography.Title>

      <Row className="position-slider-row">
        <Col span={24}>
          <label>
            {t('videoEditor.sceneItem.left')}: {values.left} (
            {Math.round((values.left / ratios.compositionWidth) * 100)}%)
          </label>
          <Slider
            min={0}
            max={ratios.compositionWidth * 2}
            step={1}
            value={values.left}
            onChange={(value) => handleSliderChange('left', value)}
            onChangeComplete={(value) => handleSliderComplete('left', value)}
          />
          <div className="quick-size-actions">
            <Button size="small" onClick={() => handleQuickChange('left', 0)}>
              0%
            </Button>
            <Button size="small" onClick={() => handleQuickChange('left', 25)}>
              25%
            </Button>
            <Button size="small" onClick={() => handleQuickChange('left', 50)}>
              50%
            </Button>
            <Button size="small" onClick={() => handleQuickChange('left', 75)}>
              75%
            </Button>
            <Button size="small" onClick={() => handleQuickChange('left', 100)}>
              100%
            </Button>
          </div>
        </Col>
      </Row>

      <Row className="position-slider-row">
        <Col span={24}>
          <label>
            {t('videoEditor.sceneItem.top')}: {values.top} (
            {Math.round((values.top / ratios.compositionHeight) * 100)}%)
          </label>
          <Slider
            min={0}
            max={ratios.compositionHeight * 2}
            step={1}
            value={values.top}
            onChange={(value) => handleSliderChange('top', value)}
            onChangeComplete={(value) => handleSliderComplete('top', value)}
          />
          <div className="quick-size-actions">
            <Button size="small" onClick={() => handleQuickChange('top', 0)}>
              0%
            </Button>
            <Button size="small" onClick={() => handleQuickChange('top', 25)}>
              25%
            </Button>
            <Button size="small" onClick={() => handleQuickChange('top', 50)}>
              50%
            </Button>
            <Button size="small" onClick={() => handleQuickChange('top', 75)}>
              75%
            </Button>
            <Button size="small" onClick={() => handleQuickChange('top', 100)}>
              100%
            </Button>
          </div>
        </Col>
      </Row>

      <Row className="position-slider-row">
        <Col span={24}>
          <label>
            {t('videoEditor.sceneItem.width')}: {values.width} (
            {Math.round((values.width / ratios.compositionWidth) * 100)}%)
          </label>
          <Slider
            min={0}
            max={ratios.compositionWidth * 2}
            step={10}
            value={values.width}
            onChange={(value) => handleSliderChange('width', value)}
            onChangeComplete={(value) => handleSliderComplete('width', value)}
          />
          <div className="quick-size-actions">
            <Button size="small" onClick={() => handleQuickChange('width', 0)}>
              0%
            </Button>
            <Button size="small" onClick={() => handleQuickChange('width', 25)}>
              25%
            </Button>
            <Button size="small" onClick={() => handleQuickChange('width', 50)}>
              50%
            </Button>
            <Button size="small" onClick={() => handleQuickChange('width', 75)}>
              75%
            </Button>
            <Button
              size="small"
              onClick={() => handleQuickChange('width', 100)}
            >
              100%
            </Button>
          </div>
        </Col>
      </Row>

      <Row className="position-slider-row">
        <Col span={24}>
          <label>
            {t('videoEditor.sceneItem.height')}: {values.height} (
            {Math.round((values.height / ratios.compositionHeight) * 100)}%)
          </label>
          <Slider
            min={0}
            max={ratios.compositionHeight * 2}
            step={10}
            value={values.height}
            onChange={(value) => handleSliderChange('height', value)}
            onChangeComplete={(value) => handleSliderComplete('height', value)}
          />
          <div className="quick-size-actions">
            <Button size="small" onClick={() => handleQuickChange('height', 0)}>
              0%
            </Button>
            <Button
              size="small"
              onClick={() => handleQuickChange('height', 25)}
            >
              25%
            </Button>
            <Button
              size="small"
              onClick={() => handleQuickChange('height', 50)}
            >
              50%
            </Button>
            <Button
              size="small"
              onClick={() => handleQuickChange('height', 75)}
            >
              75%
            </Button>
            <Button
              size="small"
              onClick={() => handleQuickChange('height', 100)}
            >
              100%
            </Button>
          </div>
        </Col>
      </Row>
    </Card>
  );

  if ([SceneItemType.Voiceover, SceneItemType.Audio].includes(sceneItem.type)) {
    return null;
  }

  return (
    <Popover
      content={positionDropdownContent}
      trigger="click"
      placement="bottom"
      overlayClassName="scene-item-position"
    >
      <Tooltip title={t('videoEditor.sceneItem.positionSize')}>
        <Button
          size={'middle'}
          type={'text'}
          icon={<LayoutOutlined />}
          title={t('videoEditor.sceneItem.positionSize')}
        />
      </Tooltip>
    </Popover>
  );
};

export default SceneItemPosition;
